/**
 * @module components/Chart/ChartAmp
 * @author Charles Blais <charles.blais@canada.ca>
 */

import { useMemo, useState } from 'react';
import ReactEcharts from 'echarts-for-react';

import { useLanguageState } from '../../contexts/language-state-provider';
import { useAppState } from '../../contexts/app-state-provider';

import {
  getToolbox, getGrid, getLegend, getTooltip,
  getXAxis, getYAxis, getDataZoom, getOnEvents,
  saToNumber, getSubtext, markerIcon,
  hideSaFromSiteDesignations,
  hidePGAFromSiteDesignations,
} from './lib';

type Props = {
  variables: HazardParameters,
  siteDesignations: SiteDesignation[],
  probability?: number,
  sa?: string[],
  vs30?: Vs30,
  initialLog?: boolean,
  initialSelected?: string[],
}


/**
 * Convert the site designations list into a list
 * that is echart friendly.
 * 
 * @param {*} siteDesignations 
 */
const makeSeries = (
  siteDesignations: SiteDesignation[],
  selectedSas: string[],
  selectedProbability: number,
  highlightVs30?: Vs30,
) => {
  // First filter any siteDesignations that don't have the same length
  // of sa units and match our selected site desgination
  const filteredSites = siteDesignations.filter((site) => {
    return Number(site.poe50) === selectedProbability;
  });


  // Divide the filteredSites into two categories
  // 1 - single value vs30 = line
  // 2 - double value vs30 = step
  const filteredSitesXV = filteredSites.filter((filteredSite) => {
    return filteredSite.vs30.length === 1;
  });
  const filteredSitesXS = filteredSites.filter((filteredSite) => {
    return filteredSite.vs30.length === 2;
  });

  // create a vertical marker
  let marker = {};
  if( highlightVs30 ){
    marker = (highlightVs30.length === 2)?
    {
      markArea: {
        data: [[{xAxis: highlightVs30[0]}, {xAxis: highlightVs30[1]}]],
        itemStyle: {
          color: '#EEE',
          borderColor: '#AAA',
          borderWidth: 1,
        }
      }
    }: {
      markLine: {
        data: [{xAxis: highlightVs30}],
        label: {show: false},
        symbol: 'none',
        lineStyle: { color: '#000', type: 'solid' },
      }
    };
  }

  const series = selectedSas.map(selectedSa => {
    const value = saToNumber(selectedSa);

    const hiddenSitesXV = hideSaFromSiteDesignations(selectedSa, filteredSitesXV);
    const hiddenSitesXS = hideSaFromSiteDesignations(selectedSa, filteredSitesXS);

    return [{
      name: `Sa(${value}) - Xv`,
      type: 'line',
      symbol: markerIcon,
      symbolSize: 10,
      markLine: {
        lineStyle: {color:'grey'},
        silent: 'true',
        symbol: ['none', 'none'],
        label: {show: false},
        color: 'red',
        data: [
          {xAxis: 140}, {xAxis: 180}, {xAxis: 360},
          {xAxis: 760}, {xAxis: 1500}, {xAxis: 3000},
        ]
      },
      markArea: {
        label: {
          padding: -6,
        },
        itemStyle: {
            color: 'none',
            borderColor: 'none',
            borderType: 'solid',
            borderWidth: 0,
        },
        data: [ 
          [{ name: 'E', xAxis: '140' }, { xAxis: '180' }], 
          [{ name: 'D', xAxis: '180' }, { xAxis: '360' }], 
          [{ name: 'C', xAxis: '360' }, { xAxis: '760' }], 
          [{ name: 'B', xAxis: '760' }, { xAxis: '1500' }], 
          [{ name: 'A', xAxis: '1500' }, { xAxis: '3000' }], 
        ]
      }
    }, {
      name: `Sa(${value}) - Xv`,
      type: 'line',
      symbol: markerIcon,
      symbolSize: 10,
      data: hiddenSitesXV.map((filteredSite) =>
        // @ts-ignore: we know the key will exist
        [filteredSite.vs30[0], filteredSite[selectedSa]]),
      ...marker,
    }, {
      name: `Sa(${value}) - Xs`,
      type: 'line',
      lineStyle: {
        type: 'dashed',
      },
      symbol: 'none',
      showSymbol: false,
      data: hiddenSitesXS.map((filteredSite) =>
        [
          // @ts-ignore: we know the key will exist
          [filteredSite.vs30[0], filteredSite[selectedSa]],
          // @ts-ignore: we know the key will exist
          [filteredSite.vs30[1], filteredSite[selectedSa]]
        ]).flat(),
      ...marker,
    }]
  }).flat();

  // remove any values where pga <= 0.001
  const hiddenSiteXS = hidePGAFromSiteDesignations(filteredSitesXS);
  const hiddenSiteXV = hidePGAFromSiteDesignations(filteredSitesXV);

  const seriesPGA = [
    {
    name: 'PGA - Xv',
    type: 'line',
    symbol: markerIcon,
    symbolSize: 10,
    data: hiddenSiteXV.map((filteredSite) =>
      [filteredSite.vs30[0], filteredSite.pga]),
    ...marker,
  }, {
    name: 'PGA - Xs',
    type: 'line',
    lineStyle: {
      type: 'dashed',
    },
    symbol: 'none',
    showSymbol: false,
    data: hiddenSiteXS.map((filteredSite) =>
      [
        [filteredSite.vs30[0], filteredSite.pga],
        [filteredSite.vs30[1], filteredSite.pga]
      ]).flat(),
    ...marker,
  },
];

  return series.concat(seriesPGA);
};


/**
 * React component
 * ===============
 */
export const ChartAmp = ({
  variables,
  siteDesignations,
  probability = 40,
  sa = [
    'sa0p05',
    'sa0p1',
    'sa0p2',
    'sa0p3',
    'sa0p5',
    'sa1p0',
    'sa2p0',
    'sa5p0',
    'sa10p0',
  ],
  initialLog = true,
  initialSelected = [
    'Sa(0.2) - Xv',
    'Sa(0.2) - Xs',
    'Sa(2) - Xv',
    'Sa(2) - Xs',
  ],
  vs30,
}: Props) => {
  const [ lang ] = useLanguageState();
  const [ state ] = useAppState();

  const [ log, setLog ] = useState(initialLog);
  // save a state for legend selection
  const [ selected, setSelected ] = useState(initialSelected);

  const series = useMemo(() =>
    makeSeries(siteDesignations, sa, probability, vs30)
  , [siteDesignations, sa, probability, vs30]);

  // The color table is now duplicated twice for each time (Xv and Xs)
  const colorTable = state.chart.colorTable.map((color) => [color, color]).flat()

  const subtext = getSubtext(variables, log, lang)

  return (
    <ReactEcharts
      style={{ height: 600 }}
      onEvents={getOnEvents(setSelected)}
      option={{
        title: {
          text: `${lang.chart.siteAmplification} - ${probability}%/50y`,
          subtext,
          textStyle: {
            fontSize: 20,
          }
        },
        color: colorTable,
        tooltip: getTooltip(),
        legend: getLegend(series, selected),
        grid: getGrid(),
        toolbox: getToolbox(lang, log, setLog),
        xAxis: getXAxis(lang.chart.vs30, log),
        yAxis: getYAxis(lang.chart.gm, log),
        dataZoom: getDataZoom(),
        series,
      }}
    />
  )
};


export default ChartAmp;
